import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import Modal from "react-modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BACKEND_URL = "https://linxlegacyacademy.dk";

export default function AdminSide() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [products, setProducts] = useState([]);
  const [newTeam, setNewTeam] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});

  // =============================
  // FETCH DATA OG INITIAL PLAYER_COUNT
  // =============================
  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/admin/users`, { credentials: "include" }).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/admin/stripe-products`, { credentials: "include" }).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/admin/teams`, { credentials: "include" }).then(res => res.json())
    ])
      .then(([userData, stripeData, teamData]) => {
        const usersList = userData.users || [];
        setUsers(usersList);
        setProducts(stripeData || []);

        const teamsWithCount = (teamData || []).map(team => {
          const count = usersList.filter(u => u.team_id === team.id).length;
          return { ...team, player_count: count };
        });
        setTeams(teamsWithCount);
      })
      .finally(() => setLoading(false));
  }, []);

  // =============================
  // FETCH EVENTS FOR VALGT HOLD
  // =============================
  useEffect(() => {
    if (!selectedTeam) return;
    fetch(`${BACKEND_URL}/api/admin/teams/${selectedTeam}/events`, { credentials: "include" })
      .then(res => res.json())
      .then(data => setEvents(data || []));
  }, [selectedTeam]);

  // =============================
  // USER FUNCTIONS
  // =============================
  const updateMembership = async (userId, priceId) => {
    await fetch(`${BACKEND_URL}/api/admin/users/${userId}/membership`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ price_id: priceId })
    });

    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, stripe_price_id: priceId } : u));
  };

  const updateUserTeam = async (userId, newTeamId) => {
    const user = users.find(u => u.user_id === userId);
    const oldTeamId = user?.team_id;

    await fetch(`${BACKEND_URL}/api/admin/users/${userId}/team`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ team_id: newTeamId || null })
    });

    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, team_id: newTeamId || null } : u));

    setTeams(prev =>
      prev.map(team => {
        if (team.id === oldTeamId) return { ...team, player_count: team.player_count - 1 };
        if (team.id === Number(newTeamId)) return { ...team, player_count: team.player_count + 1 };
        return team;
      })
    );
  };

  // =============================
  // TEAM FUNCTIONS
  // =============================
  const createTeam = async () => {
    if (!newTeam.trim()) return;

    const res = await fetch(`${BACKEND_URL}/api/admin/teams`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newTeam })
    });
    const data = await res.json();

    setTeams(prev => [...prev, { ...data, player_count: 0 }]);
    setNewTeam("");
  };

  const updateTeamName = async (teamId, name) => {
    await fetch(`${BACKEND_URL}/api/admin/teams/${teamId}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name })
    });

    setTeams(prev => prev.map(t => t.id === teamId ? { ...t, name } : t));
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm("Er du sikker på du vil slette holdet?")) return;

    await fetch(`${BACKEND_URL}/api/admin/teams/${teamId}`, {
      method: "DELETE",
      credentials: "include"
    });

    setTeams(prev => prev.filter(t => t.id !== teamId));
    setUsers(prev => prev.map(u => u.team_id === teamId ? { ...u, team_id: null } : u));
  };

  // =============================
  // EVENT MODAL FUNCTIONS
  // =============================
  const openCreateEventModal = (selectInfo) => {
    const start = selectInfo.start ? new Date(selectInfo.start) : new Date();
    const end = selectInfo.end ? new Date(selectInfo.end) : start;
    setModalData({
      title: "",
      start_time: start,
      end_time: end,
      team_id: selectedTeam,
      category: "",
      link: "",
      spil: "",
      consol: ""
    });
    setModalOpen(true);
  };

  const openEditEventModal = (event) => {
    const start = event.start ? new Date(event.start) : new Date();
    const end = event.end ? new Date(event.end) : start;
    setModalData({
      id: event.id,
      title: event.title,
      start_time: start,
      end_time: end,
      team_id: selectedTeam,
      category: event.extendedProps.category || "",
      link: event.extendedProps.link || "",
      spil: event.extendedProps.spil || "",
      consol: event.extendedProps.consol || ""
    });
    setModalOpen(true);
  };

  // =============================
  // SAVE EVENT
  // =============================
  const saveEvent = async () => {
    if (!modalData.title) return alert("Titel er påkrævet");
    if (!selectedTeam) return alert("Vælg et hold først");

    try {
      const payload = {
        ...modalData,
        start_time: modalData.start_time ? new Date(modalData.start_time).toISOString() : null,
        end_time: modalData.end_time ? new Date(modalData.end_time).toISOString() : null
      };

      console.log("payload:", payload);

      const res = modalData.id
        ? await fetch(`${BACKEND_URL}/api/admin/events/${modalData.id}`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
          })
        : await fetch(`${BACKEND_URL}/api/admin/teams/${selectedTeam}/events`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
          });

      if (!res.ok) {
        const text = await res.text();
        console.error("Backend fejl:", text);
        throw new Error(`Backend fejl: ${res.status}`);
      }

      const savedEvent = await res.json();
      setEvents(prev => modalData.id ? prev.map(e => e.id === savedEvent.id ? savedEvent : e) : [...prev, savedEvent]);
      setModalOpen(false);
    } catch (err) {
      console.error("Save event failed:", err);
      alert("Der skete en fejl. Se konsol for detaljer.");
    }
  };

  // =============================
  // DELETE EVENT
  // =============================
  const deleteEvent = async () => {
    if (!modalData.id) return;
    if (!window.confirm("Slet event?")) return;

    await fetch(`${BACKEND_URL}/api/admin/events/${modalData.id}`, {
      method: "DELETE",
      credentials: "include"
    });

    setEvents(prev => prev.filter(e => e.id !== modalData.id));
    setModalOpen(false);
  };

  // =============================
  // DRAG / RESIZE EVENT
  // =============================
  const updateEventTime = async (info) => {
    const startISO = info.event.start ? new Date(info.event.start).toISOString() : null;
    const endISO = info.event.end ? new Date(info.event.end).toISOString() : null;

    const payload = { start_time: startISO, end_time: endISO };

    const res = await fetch(`${BACKEND_URL}/api/admin/events/${info.event.id}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      console.error("Update event failed:", await res.text());
      return;
    }

    const updated = await res.json();
    setEvents(prev => prev.map(ev => ev.id === updated.id ? updated : ev));
  };

  if (loading) return <p>Indlæser...</p>;

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.user_id.toString().includes(search)
  );

  return (
    <div style={{ padding: "20px" }}>
      {/* USERS */}
      <h1>
        Admin – Spillere
        <input
          type="text"
          placeholder="Søg efter ID eller navn..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginLeft: "20px", padding: "4px", fontSize: "14px" }}
        />
      </h1>

      <table border="1" cellPadding="5" style={{ borderCollapse: "collapse", width: "100%", marginBottom: "50px" }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Navn</th>
            <th>Email</th>
            <th>Pakke</th>
            <th>Hold</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map(user => (
            <tr key={user.user_id}>
              <td>{user.user_id}</td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>
                <select value={user.stripe_price_id || ""} onChange={e => updateMembership(user.user_id, e.target.value)}>
                  <option value="">Ingen</option>
                  {products.map(product => (
                    <option key={product.price_id} value={product.price_id}>
                      {product.product_name} – {product.amount} kr
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select value={user.team_id || ""} onChange={e => updateUserTeam(user.user_id, e.target.value)}>
                  <option value="">Ingen</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* TEAMS */}
      <h1>Admin – Hold</h1>
      <div style={{ marginBottom: "20px" }}>
        <input type="text" placeholder="Nyt hold navn" value={newTeam} onChange={e => setNewTeam(e.target.value)} />
        <button onClick={createTeam} style={{ marginLeft: "10px" }}>Opret hold</button>
      </div>
      <table border="1" cellPadding="5" style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Navn</th>
            <th>Antal spillere</th>
            <th>Handling</th>
          </tr>
        </thead>
        <tbody>
          {teams.map(team => (
            <tr key={team.id}>
              <td>{team.id}</td>
              <td>
                <input value={team.name} onChange={e => updateTeamName(team.id, e.target.value)} />
              </td>
              <td><strong>{team.player_count}</strong></td>
              <td>
                <button onClick={() => deleteTeam(team.id)}>Slet</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* CALENDAR */}
      <h1>Admin – Kalender per Hold</h1>
      <select value={selectedTeam || ""} onChange={e => setSelectedTeam(Number(e.target.value))}>
        <option value="">Vælg hold</option>
        {teams.map(team => (
          <option key={team.id} value={team.id}>
            {team.name} ({team.player_count} spillere)
          </option>
        ))}
      </select>

      {selectedTeam && (
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay"
          }}
          selectable={true}
          editable={true}
          events={events.map(ev => ({
            id: ev.id,
            title: ev.title,
            start: ev.start_time,
            end: ev.end_time,
            extendedProps: {
              category: ev.category,
              link: ev.link,
              spil: ev.spil,
              consol: ev.consol
            }
          }))}
          select={openCreateEventModal}
          eventClick={info => openEditEventModal(info.event)}
          eventDrop={updateEventTime}
          eventResize={updateEventTime}
        />
      )}

      {/* MODAL */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        contentLabel="Event Modal"
        ariaHideApp={false}
        style={{ content: { maxWidth: '500px', margin: 'auto' } }}
      >
        <h2>{modalData.id ? "Rediger Event" : "Opret Event"}</h2>
        <input placeholder="Titel" value={modalData.title || ""} onChange={e => setModalData({ ...modalData, title: e.target.value })} />
        <input placeholder="Kategori" value={modalData.category || ""} onChange={e => setModalData({ ...modalData, category: e.target.value })} />
        <input placeholder="Link" value={modalData.link || ""} onChange={e => setModalData({ ...modalData, link: e.target.value })} />
        <input placeholder="Spil" value={modalData.spil || ""} onChange={e => setModalData({ ...modalData, spil: e.target.value })} />
        <input placeholder="Consol" value={modalData.consol || ""} onChange={e => setModalData({ ...modalData, consol: e.target.value })} />

        <div style={{ marginTop: "10px" }}>
          <label>Start:</label>
          <DatePicker selected={modalData.start_time} onChange={date => setModalData({ ...modalData, start_time: date })} showTimeSelect dateFormat="yyyy-MM-dd HH:mm" />
        </div>
        <div style={{ marginTop: "10px" }}>
          <label>Slut:</label>
          <DatePicker selected={modalData.end_time} onChange={date => setModalData({ ...modalData, end_time: date })} showTimeSelect dateFormat="yyyy-MM-dd HH:mm" />
        </div>
        <div style={{ marginTop: '10px' }}>
          <button onClick={saveEvent}>{modalData.id ? "Gem" : "Opret"}</button>
          {modalData.id && <button onClick={deleteEvent} style={{ marginLeft: "10px" }}>Slet</button>}
          <button onClick={() => setModalOpen(false)} style={{ marginLeft: "10px" }}>Annuller</button>
        </div>
      </Modal>
    </div>
  );
}